import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { visibleNavSections } from '@/lib/nav';
import { useCurrentUser } from '@/lib/session';

/**
 * The guided tour.
 *
 * Written against the sidebar rather than against each page, because the question it
 * answers is "where is what" - not "how do I fill in this form". A tour that walked
 * through issuing an asset would have to drive the app, and a half-finished handover
 * left behind by someone who abandoned the tour is worse than no tour.
 *
 * The middle steps are DERIVED from NAV_SECTIONS, not written again here. Every nav
 * item already carries a `description` that reads exactly like a tour step, and
 * `visibleNavSections` already filters by permission - so an administrator gets the
 * ten-stop tour and a plain employee gets the three that actually apply to them,
 * without two scripts to keep in sync. Add a nav item and it joins the tour; change
 * its description and the tour changes with it.
 *
 * No dependency. A tour library is 40kB to draw a rectangle and a card.
 */

const SEEN_KEY = 'cfl-assets:tour-seen:v1';

export interface TourStep {
  /** Matches a `data-tour` attribute. Absent means an unanchored, centred card. */
  anchor?: string;
  title: string;
  body: string;
}

/** Everything the viewer can actually reach, in sidebar order. */
function useTourSteps(): TourStep[] {
  const user = useCurrentUser();

  return React.useMemo(() => {
    const isAdmin = user.permissions.includes('manageSettings');
    const canSeeRegister = user.permissions.includes('viewRegister');

    const navSteps: TourStep[] = visibleNavSections(user.permissions).flatMap((section) =>
      section.items.map((item) => ({
        anchor: item.href,
        title: item.title,
        body: item.description,
      })),
    );

    return [
      {
        title: canSeeRegister ? 'A quick tour' : 'Welcome',
        body: canSeeRegister
          ? 'Two minutes on where everything lives. You can leave at any point with Escape, and start it again from the menu under your name.'
          : 'A quick look at what you can do here. It is short - most of this app is for the people who manage hardware; your part is confirming what you have been given and telling IT when something breaks.',
      },
      ...navSteps,
      {
        title: 'That is the whole thing',
        body: isAdmin
          ? 'Two rules worth knowing: an asset with any history cannot be deleted - retire it instead, which keeps the record - and an exit cannot be marked Cleared while that person still holds something. Both refusals are deliberate.'
          : canSeeRegister
            ? 'One rule worth knowing: anything with history is kept rather than deleted, so the register can always answer who had what and when.'
            : 'If something you were given breaks, open My assets and report it from the asset itself. IT is emailed straight away.',
      },
    ];
  }, [user.permissions]);
}

/** Remembered per browser, so it runs once and never nags. */
export function useTour() {
  const [running, setRunning] = React.useState(false);

  const start = React.useCallback(() => setRunning(true), []);

  const finish = React.useCallback(() => {
    setRunning(false);
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Private windows and blocked site data throw here. Losing the "seen" flag just
      // means the tour offers itself again - not a reason to fail.
    }
  }, []);

  // First visit only. Deliberately not on every login: a tour that reappears is the
  // thing people learn to dismiss without reading.
  React.useEffect(() => {
    let seen = true;
    try {
      seen = localStorage.getItem(SEEN_KEY) !== null;
    } catch {
      seen = true;
    }
    if (!seen) setRunning(true);
  }, []);

  return { running, start, finish };
}

export function Tour({ open, onClose }: { open: boolean; onClose: () => void }) {
  const steps = useTourSteps();
  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  const step = steps[index];
  const last = index === steps.length - 1;

  React.useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  // Re-measure on step change, and on resize/scroll while a step is showing: the
  // sidebar collapses to a rail below lg, and a highlight left at a stale position is
  // worse than none.
  React.useEffect(() => {
    if (!open || !step) return;

    const measure = () => {
      if (!step.anchor) {
        setRect(null);
        return;
      }
      const node = document.querySelector(`[data-tour="${CSS.escape(step.anchor)}"]`);
      if (!node) {
        // The anchor is not on screen - the sidebar is a drawer on mobile, or this
        // item is hidden. Fall back to the centred card rather than pointing at
        // nothing.
        setRect(null);
        return;
      }
      node.scrollIntoView({ block: 'nearest' });
      setRect(node.getBoundingClientRect());
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, step]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, steps.length - 1));
      if (event.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, steps.length]);

  if (!open || !step) return null;

  const PAD = 6;
  const CARD_W = 320;

  // The card sits to the RIGHT of a highlighted sidebar item, or centred when there is
  // nothing to point at. Clamped to the viewport so a step near the bottom does not
  // render its buttons off-screen.
  const cardStyle: React.CSSProperties = rect
    ? {
        top: Math.min(Math.max(rect.top - 8, 12), window.innerHeight - 220),
        left: Math.min(rect.right + 16, window.innerWidth - CARD_W - 12),
        width: CARD_W,
      }
    : {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: CARD_W,
      };

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label={`Tour: ${step.title}`}
    >
      {/* The dim layer. When a step has an anchor the "hole" is drawn as an enormous
          box-shadow on a transparent box sitting exactly over the target - one element
          instead of four, and it follows the element's real rounded corners. */}
      {rect ? (
        <div
          onClick={onClose}
          className="pointer-events-auto absolute rounded-lg ring-2 ring-primary transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.55)',
          }}
        />
      ) : (
        <div onClick={onClose} className="absolute inset-0 bg-black/55" />
      )}

      <div
        className="absolute rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg"
        style={cardStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-sm font-semibold leading-tight">{step.title}</h2>
          <Hint label={'Close the tour'}><button
            type="button"
            onClick={onClose}
            aria-label="Close the tour"
            className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button></Hint>
        </div>

        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{step.body}</p>

        <div className="mt-4 flex items-center gap-2">
          <span className="at-num text-[11px] text-muted-foreground">
            {index + 1} of {steps.length}
          </span>

          <div className="ml-auto flex gap-1.5">
            {index > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setIndex(index - 1)}>
                <ArrowLeft className="size-4" />
                <span className="sr-only">Back</span>
              </Button>
            )}
            {last ? (
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            ) : (
              <Button size="sm" onClick={() => setIndex(index + 1)}>
                Next <ArrowRight className="ml-1 size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
